import { defineStore } from 'pinia'
import { getArenaRooms } from '@/services/api/requests/arena'
import { extractArenaRoom, normalizeArenaStatus, type ArenaRoom } from '@/types/arena'
import { i18n } from '@/i18n'

interface ArenaListState {
  rooms: ArenaRoom[]
  loading: boolean
  error: string | null
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error && 'message' in error && typeof error.message === 'string') {
    return error.message
  }
  return fallback
}

export const useArenaListStore = defineStore('arenaList', {
  state: (): ArenaListState => ({
    rooms: [],
    loading: false,
    error: null,
  }),

  getters: {
    sortedRooms(state): ArenaRoom[] {
      return [...state.rooms].sort((a, b) => {
        const aStatus = normalizeArenaStatus(a.status)
        const bStatus = normalizeArenaStatus(b.status)

        const aWeight = aStatus === 'WAITING' || aStatus === 'READY' ? 0 : 1
        const bWeight = bStatus === 'WAITING' || bStatus === 'READY' ? 0 : 1
        if (aWeight !== bWeight) return aWeight - bWeight

        return (Date.parse(b.createdAt ?? '') || 0) - (Date.parse(a.createdAt ?? '') || 0)
      })
    },
  },

  actions: {
    async fetchRooms() {
      this.loading = true
      this.error = null

      try {
        const response = await getArenaRooms()
        this.rooms = (response.rooms ?? []).map((room) => extractArenaRoom(room))
      } catch (error) {
        this.error = getErrorMessage(error, i18n.global.t('WHALE_DUEL.store.errors.fetchRooms'))
      } finally {
        this.loading = false
      }
    },

    upsertRoom(rawRoom: unknown) {
      const room = extractArenaRoom(rawRoom)
      if (!room.id) return
      const index = this.rooms.findIndex((entry) => entry.id === room.id)
      if (index === -1) {
        this.rooms.unshift(room)
        return
      }
      this.rooms[index] = {
        ...this.rooms[index],
        ...room,
      }
    },

    patchRoom(roomId: string, patch: Partial<ArenaRoom>) {
      const index = this.rooms.findIndex((entry) => entry.id === roomId)
      if (index === -1) return
      this.rooms[index] = {
        ...this.rooms[index],
        ...patch,
      }
    },

    setParticipantReady(roomId: string, participantId: string) {
      const index = this.rooms.findIndex((entry) => entry.id === roomId)
      if (index === -1) return
      const participants = this.rooms[index].participants ?? []
      this.rooms[index].participants = participants.map((participant) =>
        participant.id === participantId ? { ...participant, isReady: true } : participant,
      )
    },
  },
})
